/**
 * tariff_v2 assembly classification (spec C4), presented in the v1
 * DecisionTreeWizard card: gradient header, FINAL CLASSIFICATION banner,
 * Classification Rationale, then the alternates with their justification,
 * clarifying questions in the wizard's question style, and the
 * Classification Path (GRI steps). Answering a question and re-running
 * replaces "walking the tree" from v1.
 */

import { useState } from 'react';
import { FiAlertTriangle, FiCheckCircle, FiHelpCircle, FiRefreshCw, FiChevronRight } from 'react-icons/fi';
import { IoCubeOutline } from 'react-icons/io5';
import { AlternatesList, ListedBanners } from './PartClassificationV2';
import { ReviewBanner, formatHtsCode, programSummary, rateText } from './shared';

export default function AssemblyClassificationV2({ result, onRerun, onReclassify, rerunning }) {
    const block = result.classification;
    const primary = block.primary;
    const { sec232, sec301 } = programSummary(primary.programs);
    const [answers, setAnswers] = useState({});

    const submitAnswers = () => onRerun(block.clarifying_questions
        .filter((q) => answers[q.question_id])
        .map((q) => ({ question_id: q.question_id, question: q.question, selected_option: answers[q.question_id] })));

    return (
        <div className="bg-white rounded-xl shadow-lg border-[3px] border-[#1e5045] overflow-hidden">
            <div className="bg-gradient-to-r from-[#1e5045] to-[#225953] px-6 py-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <IoCubeOutline className="w-6 h-6" /> Assembly HTS Classification
                </h2>
                <p className="text-green-100 text-sm mt-1">Classification of the approved assembly as one article</p>
            </div>

            <div className="p-6 space-y-6">
                <div className="bg-gradient-to-r from-[#1e5045] to-[#225953] rounded-xl p-6">
                    <div className="text-white text-sm font-medium opacity-90 mb-2">FINAL CLASSIFICATION</div>
                    <div className="text-[#f26522] text-3xl font-bold font-mono mb-1">{formatHtsCode(primary.hts.code)}</div>
                    <div className="text-green-100 text-sm mb-3">{primary.schedule.description}</div>
                    <div className="text-white text-lg font-semibold">General Duty Rate: {rateText(primary.schedule)}</div>
                    <div className="text-green-100 text-sm mt-1">Confidence: {primary.confidence}%</div>
                    <div className="flex gap-4 mt-4 flex-wrap">
                        {!!sec301.length && (
                            <div className="flex items-center gap-2 bg-amber-500/20 px-3 py-1.5 rounded-lg">
                                <FiAlertTriangle className="w-4 h-4 text-amber-300" />
                                <span className="text-amber-100 text-sm font-medium">Section 301 listed (China)</span>
                            </div>
                        )}
                        {!!sec232.length && (
                            <div className="flex items-center gap-2 bg-red-500/20 px-3 py-1.5 rounded-lg">
                                <FiAlertTriangle className="w-4 h-4 text-red-300" />
                                <span className="text-red-100 text-sm font-medium">Section 232 listed</span>
                            </div>
                        )}
                    </div>
                </div>

                <ReviewBanner needsReview={block.needs_review} reasons={block.review_reasons} />
                <ListedBanners programs={primary.programs} />

                <details className="group" open>
                    <summary className="flex items-center justify-between cursor-pointer p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <span className="font-semibold text-gray-900 flex items-center gap-2">
                            <FiCheckCircle className="w-5 h-5 text-green-600" /> Classification Rationale
                        </span>
                    </summary>
                    <div className="p-4 bg-gray-50 rounded-b-lg border-t border-gray-200 space-y-2">
                        <p className="text-gray-700 leading-relaxed">{block.whole_article_characterisation}</p>
                        <p className="text-gray-700 leading-relaxed">{primary.rationale}</p>
                        {block.changes_from_prior_run && (
                            <p className="text-gray-700"><span className="font-medium">Changed from the previous run:</span> {block.changes_from_prior_run}</p>
                        )}
                    </div>
                </details>

                <AlternatesList alternates={block.alternates} />

                {!!block.clarifying_questions.length && (
                    <div className="space-y-4">
                        {block.clarifying_questions.map((q) => (
                            <div key={q.question_id}>
                                <div className="bg-[#f6f4ed] rounded-lg p-5 border-l-4 border-[#f26522] mb-3">
                                    <div className="flex items-start gap-3">
                                        <FiHelpCircle className="w-5 h-5 text-[#f26522] mt-0.5 flex-shrink-0" />
                                        <p className="text-[#1e5045] text-lg font-semibold leading-relaxed">{q.question}</p>
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    {q.options.map((o) => (
                                        <label key={o.label} className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${answers[q.question_id] === o.label
                                            ? 'border-[#1e5045] bg-green-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}>
                                            <input type="radio" name={q.question_id} checked={answers[q.question_id] === o.label}
                                                onChange={() => setAnswers((prev) => ({ ...prev, [q.question_id]: o.label }))}
                                                className="w-5 h-5 text-[#1e5045] focus:ring-[#1e5045] focus:ring-offset-0" />
                                            <span className="text-gray-800">{o.label}</span>
                                            <span className="ml-auto font-mono text-sm text-gray-500">→ {formatHtsCode(o.hts_code)}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ))}
                        <div className="flex justify-end">
                            <button onClick={submitAnswers} disabled={rerunning || !Object.keys(answers).length}
                                className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-semibold transition-all ${rerunning || !Object.keys(answers).length
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#1e5045] text-white hover:bg-[#163a33]'}`}>
                                {rerunning ? 'Reclassifying…' : 'Reclassify with these answers'} <FiChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                )}

                <details className="group">
                    <summary className="flex items-center justify-between cursor-pointer p-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                        <span className="font-medium text-gray-700">Classification Path ({block.gri_path.length} steps)</span>
                        <span className="text-gray-500 text-sm">Click to view</span>
                    </summary>
                    <div className="p-4 bg-gray-50 rounded-b-lg border-t border-gray-200 space-y-3 text-sm">
                        {block.gri_path.map((s, i) => (
                            <div key={i}>
                                <p className={s.applied ? 'text-gray-900 font-medium' : 'text-gray-500'}>{s.rule}{s.applied ? '' : ' (not applied)'}</p>
                                <p className="text-gray-700 pl-4">→ {s.note}</p>
                            </div>
                        ))}
                        {block.essential_character && (
                            <div className="pt-2 border-t border-gray-200">
                                <p className="text-gray-900 font-medium mb-1">Essential character</p>
                                {block.essential_character.factors.map((f, i) => (
                                    <p key={i} className="text-gray-700 pl-4">→ {f.factor.replace(/_/g, ' ')} ({f.strength_of_argument}): {f.evidence}</p>
                                ))}
                                <p className="text-gray-700 pl-4 mt-1">{block.essential_character.conclusion}</p>
                            </div>
                        )}
                        <p className="text-gray-500 pt-2 border-t border-gray-200">
                            Verifier {result.verification.agrees_with_primary ? 'agrees' : 'disagrees'}: {result.verification.comment}
                        </p>
                    </div>
                </details>

                <div className="pt-4 border-t border-gray-200">
                    <button onClick={onReclassify} className="flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium text-gray-700 hover:bg-gray-100 transition-all border border-gray-300">
                        <FiRefreshCw className="w-5 h-5" /> Reclassify Assembly
                    </button>
                </div>
            </div>
        </div>
    );
}
